import { ConnectButton } from '@rainbow-me/rainbowkit'
import { useAccount, useChainId } from 'wagmi'
import { HeaderLogo } from '../common/LnsLogo'

const MONAD_TESTNET = 10143
const MONAD_MAINNET = 143

export default function AppHeader() {
  const { address, isConnected } = useAccount()
  const chainId = useChainId()
  const networkLabel = chainId === MONAD_MAINNET ? 'MAINNET' : chainId === MONAD_TESTNET ? 'TESTNET' : chainId ? `CHAIN ${chainId}` : 'NO NET'

  // Mainnet gets warm gold/orange, testnet gets cyan, other chains get purple
  const isMainnet = chainId === MONAD_MAINNET
  const isTestnet = chainId === MONAD_TESTNET

  // Derived colors for network
  const netColor  = isMainnet ? '#ff9500' : isTestnet ? '#00f5ff' : '#a855f7'
  const netBorder = isMainnet ? 'rgba(255,149,0,0.5)' : isTestnet ? 'rgba(0,245,255,0.35)' : 'rgba(168,85,247,0.35)'
  const netBg     = isMainnet ? 'rgba(255,149,0,0.1)' : isTestnet ? 'rgba(0,245,255,0.07)' : 'rgba(168,85,247,0.07)'

  return (
    <>
      <style>{`
        @keyframes lnsDotPulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.5; }
        }

        /* ── base reset for all header controls ── */
        .lns-hdr-btn {
          font-family: 'Press Start 2P', 'Courier New', monospace;
          box-sizing: border-box;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          white-space: nowrap;
          cursor: pointer;
          transition: all 0.2s ease;
          border-radius: 4px;
          text-decoration: none;
          -webkit-tap-highlight-color: transparent;
        }

        /* ── network dot pulse ── */
        .lns-net-dot { animation: lnsDotPulse 2s ease-in-out infinite; }

        /* ── hide/show helpers ── */
        .lns-net-text  { display: inline; }
        .lns-prof-text { display: inline; }

        /* ≤ 520px — hide network text, show dot only; hide profile text */
        @media (max-width: 520px) {
          .lns-net-text  { display: none !important; }
          .lns-prof-text { display: none !important; }
          .lns-hdr-net  { padding: 0 8px !important; min-width: 34px !important; gap: 0 !important; }
          .lns-hdr-prof { padding: 0 8px !important; min-width: 34px !important; gap: 0 !important; }
          .lns-hdr-wallet { padding: 0 8px !important; gap: 4px !important; }
          .lns-hdr-connect { padding: 0 10px !important; }
        }
      `}</style>

      <header
        className="app-header"
        style={{
          position: 'sticky', top: 0, zIndex: 1000,
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'nowrap',
          gap: '0.25rem',
          padding: 'clamp(0.4rem, 1.2vw, 0.6rem) clamp(0.5rem, 2.5vw, 1.25rem)',
          background: 'rgba(10, 10, 15, 0.97)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: '2px solid rgba(0, 245, 255, 0.18)',
          boxShadow: '0 2px 20px rgba(0,0,0,0.6)',
          minHeight: '60px',
          overflow: 'hidden',
        }}
      >
        {/* Scanline + bottom accent */}
        <div style={{ position:'absolute', inset:0, pointerEvents:'none', background:'repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(0,245,255,0.012) 2px,rgba(0,245,255,0.012) 4px)' }} />
        <div style={{ position:'absolute', bottom:0, left:0, right:0, height:'1px', pointerEvents:'none', background:'linear-gradient(90deg,transparent,rgba(0,245,255,0.4),rgba(168,85,247,0.3),transparent)' }} />

        {/* Logo — pixel diamond + text lockup; tap target for mobile */}
        <a
          href="/#/"
          style={{
            position: 'relative',
            textDecoration: 'none',
            flexShrink: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            minHeight: 44,
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <HeaderLogo />
        </a>

        {/* Right controls */}
        <div style={{ position:'relative', display:'flex', alignItems:'center', gap:'0.5rem', flexShrink:0 }}>

          {/* ── Wallet / Connect ── */}
          <ConnectButton.Custom>
            {({ openConnectModal, openAccountModal, mounted }) => {
              if (!mounted) return null
              return isConnected && address ? (
                <div style={{ display:'flex', alignItems:'center', gap:'0.25rem' }}>
                  {/* Wallet */}
                  <button
                    type="button"
                    className="lns-hdr-btn lns-hdr-wallet"
                    onClick={openAccountModal}
                    title={`Connected: ${address}`}
                    style={{
                      gap: '0.35rem',
                      padding: '0 0.6rem',
                      height: 40,
                      minHeight: 40,
                      maxHeight: 40,
                      lineHeight: 1,
                      border: '2px solid rgba(0,245,255,0.55)',
                      background: 'rgba(0,245,255,0.08)',
                      color: '#00f5ff',
                      fontSize: 'clamp(0.46rem, 1.1vw, 0.58rem)',
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                      boxShadow: '0 0 10px rgba(0,245,255,0.15)',
                      textShadow: '0 0 6px rgba(0,245,255,0.5)',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background='rgba(0,245,255,0.15)'; e.currentTarget.style.boxShadow='0 0 16px rgba(0,245,255,0.3)' }}
                    onMouseLeave={(e) => { e.currentTarget.style.background='rgba(0,245,255,0.08)'; e.currentTarget.style.boxShadow='0 0 10px rgba(0,245,255,0.15)' }}
                  >
                    {/* Live dot */}
                    <span className="lns-net-dot" style={{ width:6, height:6, borderRadius:1, background:'#00f5ff', flexShrink:0, boxShadow:'0 0 5px rgba(0,245,255,0.9)' }} />
                    <span>CONNECTED</span>
                  </button>

                </div>
              ) : (
                <button
                  type="button"
                  className="lns-hdr-btn lns-hdr-connect"
                  onClick={openConnectModal}
                  style={{
                    gap: '0.3rem',
                    padding: '0 0.875rem',
                    height: 40,
                    minHeight: 40,
                    maxHeight: 40,
                    lineHeight: 1,
                    border: '2px solid #00f5ff',
                    background: 'linear-gradient(135deg, rgba(0,245,255,0.14), rgba(0,245,255,0.04))',
                    color: '#00f5ff',
                    fontSize: 'clamp(0.5rem, 1.4vw, 0.62rem)',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    boxShadow: '0 0 12px rgba(0,245,255,0.25)',
                    textShadow: '0 0 8px rgba(0,245,255,0.6)',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background='rgba(0,245,255,0.18)'; e.currentTarget.style.boxShadow='0 0 20px rgba(0,245,255,0.4)' }}
                  onMouseLeave={(e) => { e.currentTarget.style.background='linear-gradient(135deg,rgba(0,245,255,0.14),rgba(0,245,255,0.04))'; e.currentTarget.style.boxShadow='0 0 12px rgba(0,245,255,0.25)' }}
                >
                  CONNECT
                </button>
              )
            }}
          </ConnectButton.Custom>

          {/* ── Network badge ── */}
          <span
            className="lns-hdr-btn lns-hdr-net"
            style={{
              gap: '0.35rem',
              padding: '0 0.6rem',
              height: 40,
              minHeight: 40,
              maxHeight: 40,
              lineHeight: 1,
              border: `2px solid ${netBorder}`,
              background: netBg,
              color: netColor,
              fontSize: 'clamp(0.46rem, 1.1vw, 0.58rem)',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              textShadow: `0 0 6px ${netColor}80`,
              boxShadow: `0 0 8px ${netColor}22`,
              cursor: 'default',
            }}
          >
            {/* Pulsing dot */}
            <span className="lns-net-dot" style={{ width:6, height:6, borderRadius:1, flexShrink:0, background: netColor, boxShadow:`0 0 5px ${netColor}` }} />
            <span className="lns-net-text">{networkLabel}</span>
          </span>

          {/* ── Profile ── */}
          {isConnected && address && (
            <a
              href="/#/profile"
              className="lns-hdr-btn lns-hdr-prof"
              style={{
                gap: '0.35rem',
                padding: '0 0.75rem',
                height: 40,
                minHeight: 40,
                maxHeight: 40,
                lineHeight: 1,
                border: '2px solid rgba(168,85,247,0.55)',
                background: 'rgba(168,85,247,0.12)',
                color: '#c084fc',
                fontSize: 'clamp(0.5rem, 1.4vw, 0.62rem)',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                textShadow: '0 0 8px rgba(168,85,247,0.5)',
                boxShadow: '0 0 8px rgba(168,85,247,0.15)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background='rgba(168,85,247,0.22)'; e.currentTarget.style.boxShadow='0 0 16px rgba(168,85,247,0.35)'; e.currentTarget.style.color='#d8b4fe' }}
              onMouseLeave={(e) => { e.currentTarget.style.background='rgba(168,85,247,0.12)'; e.currentTarget.style.boxShadow='0 0 8px rgba(168,85,247,0.15)'; e.currentTarget.style.color='#c084fc' }}
            >
              {/* User icon using safe ASCII */}
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ flexShrink:0 }}>
                <circle cx="5" cy="3" r="2" fill="#c084fc" />
                <path d="M1 9c0-2.2 1.8-4 4-4s4 1.8 4 4" stroke="#c084fc" strokeWidth="1.2" fill="none" />
              </svg>
              <span className="lns-prof-text">ACCOUNT</span>
            </a>
          )}

        </div>
      </header>
    </>
  )
}
